import React, { FC, useState } from "react";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { EnrolmentDevice } from "../../models/EnrolmentDevices";
import { EnrolmentTask } from "../../models/EnrolmentTask";
import { PluginCommand } from "../../models/PluginCommand";
import SystemEnrolmentDevice from "../../models/SystemEnrolmentDevice";
import { IShowEnrollValidOptions } from "../../services/wsPluginService/plugins/base/pluginModels";
import EmptyTableState from "../emptyTableState/EmptyTableState";
import List from '../list/list';
import { WizardStepLayout } from '../wizard/wizard-summary-layout';

interface Props {
    onBack: (event: any) => void;
    onNext: (event: any) => void;
    enrollMethod: string;
    pluginsAvailable?: IShowEnrollValidOptions;
    availableSystemDeviceIds?: number[];
    availabilityResolved?: boolean;
    selectedBiometricBrand?: string;
    excludeOptionIds?: number[];
}

const SubTasksForm: FC<Props> = ({ onNext, onBack, enrollMethod, pluginsAvailable, availableSystemDeviceIds, availabilityResolved = true, selectedBiometricBrand, excludeOptionIds = [] }) => {
    const [taskSelected, setTaskSelected] = useState(false);
    const [task, setTask] = useState('');
    const { translate } = useLocale();
    const enrolmentDevice = new EnrolmentDevice(translate);
    const enrolmentTask = new EnrolmentTask(translate);

    const getAllDevices = (pluginsAvailable?: IShowEnrollValidOptions, availableSystemDeviceIds?: number[]) => {
        let alwaysAvailableDevices: any[] = [];
        let plugins: PluginCommand[] = [];
        let devices: SystemEnrolmentDevice[] = [];
        const brand = selectedBiometricBrand;

        if (enrollMethod === enrolmentTask.ENROL_CARD.value) {
            alwaysAvailableDevices.push(enrolmentDevice.MANUAL_CODE_TYPE);
            alwaysAvailableDevices.push(enrolmentDevice.RANDOM_PAC);
            plugins = enrolmentDevice.ENROL_CARD_PLUGIN;
            devices = enrolmentDevice.ENROL_CARD_DEVICES;
        } else if (enrollMethod === enrolmentTask.ENROL_FACE.value) {
            plugins = enrolmentDevice.ENROL_FACE_PLUGIN;
            devices = enrolmentDevice.ENROL_FACE_DEVICES;
        } else if (enrollMethod === enrolmentTask.ENROL_HAND.value) {
            plugins = enrolmentDevice.ENROL_HAND_PLUGIN;
            devices = enrolmentDevice.ENROL_HAND_DEVICES;
        } else if (enrollMethod === enrolmentTask.ENROL_FINGERPRINT.value) {
            plugins = enrolmentDevice.ENROL_FINGER_PLUGIN;
            devices = enrolmentDevice.ENROL_FINGER_DEVICES;
        } else if (enrollMethod === enrolmentTask.ENROL_MOBILE_CREDENTIAL.value) {
            alwaysAvailableDevices.push(enrolmentDevice.HID_MOBILE_ACCESS);
            alwaysAvailableDevices.push(enrolmentDevice.STID_MOBILE);
        } else if (enrollMethod === enrolmentTask.ENCODE_CARD.value) {
            alwaysAvailableDevices.push(enrolmentDevice.VINGCARD);
        }

        const filterByBrand = <T extends { brand?: string }>(items: T[]) => {
            if (!brand) {
                return items;
            }
            return items.filter((item) => item.brand === brand);
        };

        const allDevices = [
            ...alwaysAvailableDevices,
            ...filterByBrand(plugins),
            ...filterByBrand(devices)
        ];

        const isAvailable = (item: any): boolean => {
            const isPluginItem = (item as PluginCommand).type !== undefined;
            if (isPluginItem) {
                if (!availabilityResolved) {
                    return false;
                }

                const pluginItem = item as PluginCommand;
                const pluginType = pluginItem.type;
                const isPluginReportedAvailable = !!(pluginType && pluginsAvailable?.hasOwnProperty(pluginType) && pluginsAvailable[pluginType] === true);

                if (!isPluginReportedAvailable) {
                    return false;
                }

                // Some webcam/plugin flows still require at least one installed target device for template validation.
                if (pluginItem.requiredSystemDeviceIds && pluginItem.requiredSystemDeviceIds.length > 0) {
                    return pluginItem.requiredSystemDeviceIds.some((id) => availableSystemDeviceIds?.includes(id));
                }

                return true;
            }

            const isSystemDeviceOption = devices.some(device => device.id === item.id);
            if (isSystemDeviceOption) {
                if (!availabilityResolved) {
                    return false;
                }
                return !!availableSystemDeviceIds?.includes(item.id);
            }

            return true;
        };

        return allDevices
            .filter((item) => !excludeOptionIds.includes(item.id))
            .map(item => ({ item, isAvailable: isAvailable(item) }));
    };

    const allDevices = getAllDevices(pluginsAvailable, availableSystemDeviceIds)

    const onLocalNext = (event: any) => {
        if (event.task) {
            onNext({ ...event, task: event.task, description: event.description });
        }
    }

    const itemSelected = (item: any) => {
        const selectedItem = item.props['data-item'];
        const disabled = item.props['data-disabled'];
        if (selectedItem) {
            if (disabled) {
                return;
            }
            setTaskSelected(true);
            setTask(selectedItem.value);
            onLocalNext({task: selectedItem.value, description: selectedItem.label, setTaskSelected: setTaskSelected});
            setTaskSelected(false);
            setTask('');
        }
    }

    const getListItems = (): { value: JSX.Element; isSelected: boolean; }[] => {
        return allDevices.map((deviceOption) => {
            const item = deviceOption.item;
            const available = deviceOption.isAvailable;
            return {
                value: (
                    <div
                        className={`flex flex-row items-center select-none ${available ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                        data-item={item}
                        data-disabled={!available}>
                        <div id={`subTask${item.label.split(' ').join('')}`} className="p-2">{item.label}</div>
                    </div>
                ),
                isSelected: item.value === task
            };
        })
    }

    return (
        <WizardStepLayout onBack={onBack} onNext={(e) => onLocalNext(e)} nextDisabled={!taskSelected}>
            <div className="overflow-y-auto pr-1 mb-16">
                {!availabilityResolved ?
                    <div className="mb-2 px-2 text-xs text-gray-500">{translate.byKey(TranslationKeys.loadingDevices)}</div>
                    : null}
                {allDevices.length > 0 ?
                    <List onSelect={(_, item) => itemSelected(item)} isStriped items={getListItems()} />
                    : <EmptyTableState message={translate.byKey(TranslationKeys.noEnrolmentDeviceFound)} showAddNewButton={true} />
                }
            </div>
        </WizardStepLayout>
    )
}

export default SubTasksForm;
